import { Box, Card, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useId, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';
import { deleteContractFile } from '../../../../redux/slices/contracts';
import { useDispatch } from '../../../../redux/store';
import Iconify from '../../../../reusable-components/iconify';
import PDFViewer from '../../../../reusable-components/pdf-viewer/PDFViewer';
import { formCardStyle } from '../contractStyles';
import { CONTRACT_FILE_TYPES } from './contractFileTypesEnum';


const ACCEPTED_FILE_TYPES = {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/msword': ['.doc'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

ContractFileCard.propTypes = {
    activeTab: PropTypes.number,
    onTabChange: PropTypes.func,
    contractFile: PropTypes.instanceOf(Blob),
    terminationFile: PropTypes.instanceOf(Blob),
    contractFileUrl: PropTypes.string,
    terminationFileUrl: PropTypes.string,
    contractFileName: PropTypes.string,
    terminationFileName: PropTypes.string,
    onUploadContract: PropTypes.func,
    onUploadTermination: PropTypes.func,
    isEditable: PropTypes.bool,
};

function useFileUrl(file) {
    const [url, setUrl] = useState(null);
    useEffect(() => {
        if (!file) {
            setUrl(null);
            return undefined;
        }
        const nextUrl = URL.createObjectURL(file);
        setUrl(nextUrl);
        return () => URL.revokeObjectURL(nextUrl);
    }, [file]);
    return url;
}

// Upload callbacks reject on failure; the parent supplies the downloaded file or preview URL.
export default function ContractFileCard({
    activeTab = CONTRACT_FILE_TYPES.CONTRACT_LETTER,
    onTabChange,
    contractFile,
    terminationFile,
    contractFileUrl = null,
    terminationFileUrl = null,
    contractFileName = 'Contract.pdf',
    terminationFileName = 'Termination.pdf',
    onUploadContract,
    onUploadTermination,
    isEditable = true
}) {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { getValues } = useFormContext();
    const [uploading, setUploading] = useState(false);
    const uploadInProgress = useRef(false);
    const contractBlobUrl = useFileUrl(contractFile);
    const terminationBlobUrl = useFileUrl(terminationFile);
    const deleteInProgress = useRef(false);
    const cardId = useId();
    const files = [
        { fileType: CONTRACT_FILE_TYPES.CONTRACT_LETTER, label: 'Contract File', url: contractBlobUrl || contractFileUrl, name: contractFileName, onUpload: onUploadContract },
        { fileType: CONTRACT_FILE_TYPES.TERMINATION_LETTER, label: 'Termination File', url: terminationBlobUrl || terminationFileUrl, name: terminationFileName, onUpload: onUploadTermination },
    ];
    const activeFile = files[activeTab];
    const uploadDisabled = uploading || !activeFile.onUpload || Boolean(activeFile.url) || !isEditable;

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: ACCEPTED_FILE_TYPES,
        multiple: false,
        disabled: uploadDisabled,
        onDropAccepted: async ([file]) => {
            if (!file || uploadDisabled || uploadInProgress.current) return;
            uploadInProgress.current = true;
            setUploading(true);
            try {
                await activeFile.onUpload(file);
            } catch (error) {
                // The Redux thunk displays the failure notification.
            } finally {
                uploadInProgress.current = false;
                setUploading(false);
            }
        },
        onDropRejected: () => {
            enqueueSnackbar('Please upload one PDF, Word, or Excel file.', { variant: 'error' });
        },
    });

    const handleDelete = async (fileType) => {
        if (uploading || deleteInProgress.current) return;
        deleteInProgress.current = true;
        try {
            await dispatch(deleteContractFile(getValues('id'), fileType));
        } catch (error) {
            // The Redux thunk displays the failure notification.
        } finally {
            deleteInProgress.current = false;
        }
    };

    return (
        <Card sx={{ p: 2, ...formCardStyle }}>
            <Tabs
                value={activeTab}
                onChange={(_, value) => onTabChange?.(value)}
                aria-label="Contract files"
                sx={{ minHeight: 32 }}
            >
                {files.map((file) => (
                    <Tab
                        key={file.label}
                        label={file.label}
                        value={file.fileType}
                        id={`${cardId}-tab-${file.fileType}`}
                        aria-controls={`${cardId}-panel-${file.fileType}`}
                        disabled={uploading}
                        sx={{ minHeight: 32 }}
                    />
                ))}
            </Tabs>
            {files.map((file) => (
                <Box
                    key={file.label}
                    role="tabpanel"
                    id={`${cardId}-panel-${file.fileType}`}
                    aria-labelledby={`${cardId}-tab-${file.fileType}`}
                    hidden={activeTab !== file.fileType}
                    sx={{ mt: '10px' }}
                >
                    {file.url ? (
                        // Keep viewers mounted across tab changes to preserve their blob URLs.
                        // PDFViewer appends .pdf to the supplied download name.
                        <PDFViewer splitPDF={false} key={file.url} blobUrl={file.url} fileName={file.name.replace(/\.pdf$/i, '')}
                            handleDelete={!isEditable ? null : () => handleDelete(file.fileType)} />
                    ) : (
                        activeTab === file.fileType && (
                            <Box
                                {...getRootProps({
                                    role: 'button',
                                    'aria-label': `Upload ${file.label.toLowerCase()}`,
                                    'aria-disabled': uploadDisabled,
                                    'aria-busy': uploading,
                                })}
                                sx={{
                                    width: '100%',
                                    height: '430px',
                                    border: `dashed 1px ${uploadDisabled ? theme.palette.action.disabled : theme.palette.info.main}`,
                                    cursor: uploadDisabled ? 'default' : 'pointer',
                                    bgcolor: isDragActive ? 'action.hover' : 'transparent',
                                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                                }}
                            >
                                <input {...getInputProps()} />
                                <Stack spacing={1} justifyContent="center" alignItems="center" height="100%">
                                    {uploading ? (
                                        <CircularProgress aria-label="Uploading file" />
                                    ) : (
                                        <Iconify
                                            icon="arcticons:folder-upload"
                                            width="5rem"
                                            height="5rem"
                                            sx={{ color: uploadDisabled ? theme.palette.action.disabled : theme.palette.primary.main }}
                                        />
                                    )}
                                    <Typography color="text.secondary" role="status">
                                        {uploading
                                            ? 'Uploading file...'
                                            : uploadDisabled
                                                ? 'No file uploaded. Upload is unavailable.'
                                                : `Drop ${file.label.toLowerCase()} or upload file`}
                                    </Typography>
                                </Stack>
                            </Box>
                        )
                    )}
                </Box>
            ))}
        </Card>
    );
}
